import { type Format } from '@ugin/shared';
import { Radar } from 'lucide-react';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { type ArchetypeBucket } from '@/api/types';
import { useStats } from '@/hooks/useArena';
import { formatRecord, shareText, winRateText } from '@/lib/stats';


const meta: WidgetMeta = {
  id: 'personal-meta',
  name: 'What you are facing',
  description: 'Your ladder, not the published one.',
  icon: Radar,
  defaultSize: { w: 5, h: 5 },
  category: 'meta',
};

export interface PersonalMetaWidgetProps {
  format?: Format;
}

/**
 * The scraped meta is the prior; what Rowan actually queues into is the
 * posterior. Showing both side by side hands him a fact about his own
 * experience that no website can assemble for him.
 */
export const PersonalMetaWidget: WidgetComponent<PersonalMetaWidgetProps> = ({ format }) => {
  const { data, isLoading, error, refetch } = useStats(format);
  const rows = data?.byArchetype ?? [];

  return (
    <WidgetContainer
      meta={meta}
      icon={<Radar className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && rows.length === 0}
      emptyTitle="Nothing to compare yet"
      emptyDescription="Once a few opponents are classified, Ugin will show what your ladder actually looks like against what the sites report."
    >
      {rows.length > 0 ? (
        <>
          <ul className="space-y-1.5">
            {rows.map((row) => (
              <li key={row.archetype ?? 'unclassified'}>
                <ArchetypeRow row={row} />
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-ugin-text-faint">
            Faced is your share of these matches. Published is what the metagame sites report.
          </p>
        </>
      ) : null}
    </WidgetContainer>
  );
};

PersonalMetaWidget.meta = meta;

function ArchetypeRow({ row }: { row: ArchetypeBucket }) {
  const faced = Math.round(row.facedShare * 100);
  return (
    <div className="rounded-lg border border-ugin-border/40 bg-ugin-surface/30 px-3 py-2">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className={row.archetype === null ? 'italic text-ugin-text-muted' : 'text-ugin-text'}>
          {row.archetype ?? 'Unclassified'}
        </span>
        <span className="flex items-baseline gap-3 font-mono text-xs">
          <span className="text-ugin-text-muted">{formatRecord(row)}</span>
          <span
            className="w-10 text-right text-ugin-accent"
            title={row.provisional ? `Provisional — ${String(row.n)} of 20 matches` : undefined}
          >
            {winRateText(row.winRate)}
          </span>
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ugin-bg-deep">
          <div
            className="h-full rounded-full bg-ugin-mystic"
            style={{ width: `${String(Math.min(100, faced))}%` }}
          />
        </div>
        <span className="w-24 text-right font-mono text-xs text-ugin-text-faint">
          <span className="text-ugin-text-muted">{faced}%</span> faced ·{' '}
          {shareText(row.publishedShare)}
        </span>
      </div>
    </div>
  );
}
